import { Link, useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  useReceipts,
  usePendingReceipts,
  useCuentasFiltro,
} from "../hooks/useReceipt";
import { downloadPDF } from "../services/receiptService";
import apiFetch from "../../../../shared/services/apiClient";
import { uploadExcel } from "../../shared/excelService";

import TableHeader from "../components/TableHeader";
import TableBody from "../components/TableBody";
import TableFooter from "../components/TableFooter";
import ImportExportBar from "../../shared/ImportExportBar";
import PendingReceiptsTable from "../../shared/PendingReceiptsTable";

import styles from "../styles/receipt.module.css";
import SendReceiptEmailModal from "../components/SendReceiptEmailModal";
import {
  DeleteReceiptModal,
  EditReceiptModal,
} from "../components/ReceiptActionsModal";

const INITIAL_PARAMS = {
  search: "",
  fecha_desde: "",
  fecha_hasta: "",
  monto_min: "",
  monto_max: "",
  metodo_pago: [],
  cuenta_bancaria: [],
  ordering: "",
};

export default function ListReceipt() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [params, setParams] = useState(INITIAL_PARAMS);

  const [importResult, setImportResult] = useState(null);
  const [importPending, setImportPending] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const isContador = localStorage.getItem("rol") === "CONTADOR";
  // El botón de Saldos Iniciales se habilita para CONTADOR y para el rol
  // ADMINISTRADOR (cualquier rol que no sea CONTADOR ni RESIDENTE — igual que
  // el label del Sidebar) y para superusuarios.
  const _rol = localStorage.getItem("rol") || "USUARIO";
  const _isSuperUser = localStorage.getItem("is_superuser") === "true";
  const puedeSaldoInicial = isContador || _isSuperUser || (_rol !== "RESIDENTE" && _rol !== "CONTADOR");
  const [showSaldoInicial, setShowSaldoInicial] = useState(false);
  const [emailModal, setEmailModal] = useState(null);
  const [deletingReceipt, setDeletingReceipt] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [selectedMap, setSelectedMap] = useState(new Map());
  const [selectingAll, setSelectingAll] = useState(false);
  const selectedIds = new Set(selectedMap.keys());

  function handleParamsChange(newParams) {
    setParams(newParams);
    setPage(1);
    setSelectedMap(new Map());
  }

  function toggleSelect(id, receiptObj) {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, receiptObj);
      return next;
    });
  }

  async function selectAll() {
    setSelectingAll(true);
    try {
      const res = await apiFetch("/api/finance/recibo/?page_size=9999");
      const data = await res.json();
      const all = data.results ?? [];
      const map = new Map();
      all.forEach((r) => map.set(r.id, r));
      setSelectedMap(map);
    } catch (_) {
    } finally {
      setSelectingAll(false);
    }
  }

  function clearSelection() {
    setSelectedMap(new Map());
  }

  const { receipts, count, next, previous, loading } = useReceipts(
    page,
    params,
    refreshKey,
  );
  const { pending } = usePendingReceipts(refreshKey);
  const cuentas = useCuentasFiltro();
  const totalPages = Math.ceil(count / 8);

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    setRefreshKey((k) => k + 1);
  }, []);

  function handleResult(resultado, pendientesNuevos) {
    setImportResult(resultado);
    setImportPending(pendientesNuevos ?? []);
  }

  function dismissResult() {
    setImportResult(null);
    setImportPending([]);
  }

  return (
    <>
      {/* ── Breadcrumb ─────────────────────────────────────────────── */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxc">
          <span className={styles.inactive}> {t("recibos.cuentasPorCobrar")} /</span>
        </Link>
        <span className={styles.active}> {t("recibos.titulo")}</span>
      </span>

      {/* ── Fila header ────────────────────────────────────────────── */}
      <div
        className="d-flex justify-content-between align-items-start flex-wrap gap-2"
        style={{ marginBottom: "6px" }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 className="mt-2 me-2 p-0 title">{t("recibos.gestion")}</h2>
            {pending.length > 0 && (
              <span
                style={{
                  background: "rgba(249,115,22,0.15)",
                  border: "1px solid rgba(249,115,22,0.35)",
                  color: "#f97316",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                }}
              >
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{ marginRight: 5 }}
                />
                {pending.length} {t("recibos.sinAplicar")}
              </span>
            )}
          </div>

          <p className="mb-3" style={{ color: "var(--text-muted)" }}>{t("recibos.administra")}</p>
        </div>

        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
            <ImportExportBar
              plantillaUrl="/api/finance/recibo/plantilla-excel/"
              importarUrl="/api/finance/recibo/importar-excel/"
              exportarUrl="/api/finance/recibo/exportar-excel/"
              plantillaFilename="plantilla_recibos.xlsx"
              exportarFilename="recibos.xlsx"
              onImportSuccess={handleImportSuccess}
              onResult={handleResult}
              isReceipts={true}
            />
            {puedeSaldoInicial && (
              <button
                onClick={() => setShowSaldoInicial(true)}
                className="btn d-flex gap-2 align-items-center"
                style={{
                  background: "rgba(139,92,246,0.15)",
                  border: "1px solid rgba(139,92,246,0.35)",
                  color: "#a78bfa",
                  fontWeight: 600,
                  fontSize: "0.84rem",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "rgba(139,92,246,0.25)";
                  e.currentTarget.style.borderColor = "rgba(139,92,246,0.6)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "rgba(139,92,246,0.15)";
                  e.currentTarget.style.borderColor = "rgba(139,92,246,0.35)";
                }}
              >
                <i className="fa-solid fa-scale-balanced" /> Saldos Iniciales
              </button>
            )}
            <Link
              to="/finanzas/recibos/agregar"
              className={`btn ${styles["btn-add-receipt"]} d-flex gap-2 align-items-center`}
            >
              <i className="fa-solid fa-plus" /> {t("common.agregar")}
            </Link>
          </div>
          <div className="d-flex gap-2 align-items-center justify-content-end flex-wrap">
            {selectedIds.size > 0 && (
              <>
                <span style={{ color: "var(--text-secondary)", fontSize: "0.82rem" }}>
                  {selectedIds.size} {t("common.seleccionados")}
                </span>

                <button
                  className="btn"
                  onClick={clearSelection}
                  style={{
                    background: "transparent",
                    color: "var(--text-secondary)",
                    border: "1px solid var(--border-card)",
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#ef4444";
                    e.currentTarget.style.color = "#ef4444";
                    e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-card)";
                    e.currentTarget.style.color = "var(--text-secondary)";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{" "}
                  {t("common.deseleccionarTodas")}
                </button>
                <button
                  className="btn d-flex gap-2 align-items-center"
                  onClick={() =>
                    setEmailModal(Array.from(selectedMap.values()))
                  }
                  style={{
                    background: "#f97316",
                    color: "#fff",
                    border: "1px solid #f97316",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#ea580c")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f97316")
                  }
                >
                  <i className="fa-solid fa-envelope" /> {t("common.enviarCorreos")} (
                  {selectedIds.size})
                </button>
              </>
            )}
            {selectedIds.size !== count && (
              <button
                className="btn d-flex gap-2 align-items-center"
                onClick={selectAll}
                disabled={selectingAll || count === 0}
                style={{
                  background: "var(--hover-bg)",
                  color: "var(--text-secondary)",
                  border: "none",
                  fontSize: "0.8rem",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
                onMouseOut={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
              >
                {selectingAll ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" />{" "}
                    {t("common.seleccionando")}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check-double" /> {t("common.seleccionarTodas")}
                    ({count})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Modal Saldos Iniciales ─────────────────────────────────── */}
      {showSaldoInicial && (
        <SaldoInicialModal
          onClose={() => setShowSaldoInicial(false)}
          onSuccess={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {/* ── Modal Estado de Cuenta PDF ─────────────────────────────── */}
      {showPdfModal && <PdfModal onClose={() => setShowPdfModal(false)} />}

      {emailModal && (
        <SendReceiptEmailModal
          recibos={emailModal}
          onClose={() => setEmailModal(null)}
          onSuccess={() => {
            setActionMsg({ ok: true, text: t("recibos.correosEnviados") });
            setTimeout(() => setActionMsg(null), 4000);
            clearSelection();
          }}
        />
      )}
      {deletingReceipt && (
        <DeleteReceiptModal
          receipt={deletingReceipt}
          onClose={() => setDeletingReceipt(null)}
          onSuccess={(msg) => {
            setActionMsg({ ok: true, text: msg });
            setTimeout(() => setActionMsg(null), 4000);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
      {editingReceipt && (
        <EditReceiptModal
          receipt={editingReceipt}
          onClose={() => setEditingReceipt(null)}
          onSuccess={(msg) => {
            setActionMsg({ ok: true, text: msg });
            setTimeout(() => setActionMsg(null), 4000);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {/* ── Resultado de importación ───────────────────────────────── */}
      {importResult && (
        <ImportResultBanner
          result={importResult}
          pending={importPending}
          onDismiss={dismissResult}
          onNavigate={(id) => navigate(`/finanzas/recibos/${id}/aplicar`)}
        />
      )}

      {/* ── Pendientes ─────────────────────────────────────────────── */}
      <PendingReceiptsTable
        pendientes={pending}
        pageSize={5}
        title={
          pending.length > 0
            ? `${pending.length} recibo(s) sin facturas aplicadas`
            : undefined
        }
      />

      {/* ── Tabla principal ────────────────────────────────────────── */}
      <div className={styles["table-container"]}>
        <TableHeader
          params={params}
          onParamsChange={handleParamsChange}
          cuentas={cuentas}
          loading={loading}
        />
        <TableBody
          receipts={receipts}
          onEdit={(r) => setEditingReceipt(r)}
          onDelete={(r) => setDeletingReceipt(r)}
          onEmail={(r) => setEmailModal([r])}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
        />
        <TableFooter
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          next={next}
          previous={previous}
          count={count}
          loading={loading}
        />
      </div>

      {/* ── Toast flotante ─────────────────────────────────────────── */}
      {actionMsg && (
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 2000,
          background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(239,68,68,0.07)",
          border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
          borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#f87171"}`,
          borderRadius: 8, padding: "12px 18px",
          display: "flex", alignItems: "center", gap: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)", maxWidth: 380,
        }}>
          <i
            className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
            style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontSize: "1.1rem" }}
          />
          <span style={{ color: actionMsg.ok ? "#4ade80" : "#f87171", fontWeight: 600, fontSize: "0.88rem" }}>
            {actionMsg.text}
          </span>
        </div>
      )}
    </>
  );
}

// ── Modal Estado de Cuenta PDF ────────────────────────────────────────────
function PdfModal({ onClose }) {
  const { t } = useTranslation();
  const [filiales, setFiliales] = useState([]);
  const [filial, setFilial] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFinal, setFechaFinal] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Cargar filiales reales del condominio
  useEffect(() => {
    apiFetch("/api/residents/filial/")
      .then((r) => r.json())
      .then((data) => {
        const results = data.results ?? data;
        setFiliales(results);
        if (results.length > 0) setFilial(String(results[0].id));
      })
      .catch(() => {});
  }, []);

  async function handleGenerate() {
    if (!filial || !fechaInicio || !fechaFinal) {
      setError(t("common.confirmar"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const blob = await downloadPDF({
        filial,
        fecha_inicio: fechaInicio,
        fecha_final: fechaFinal,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filialLabel =
        filiales.find((f) => String(f.id) === filial)?.idFilial ?? filial;
      a.download = `estado_cuenta_${filialLabel}_${fechaInicio.replace(/\//g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch {
      setError(t("common.error"));
    } finally {
      setLoading(false);
    }
  }

  const fieldLabel = {
    display: "block",
    color: "var(--text-muted)",
    fontSize: "0.72rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: 5,
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    background: "var(--bg-input)",
    border: "1px solid var(--border-card)",
    borderRadius: 6,
    color: "var(--text-primary)",
    fontSize: "0.85rem",
    colorScheme: "light dark",
    outline: "none",
    transition: "border-color 0.15s",
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.65)",
          zIndex: 1000,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          background: "var(--bg-card)",
          border: "1px solid var(--border-card)",
          borderRadius: 12,
          padding: "24px 24px 20px",
          width: "min(440px, 93vw)",
          zIndex: 1001,
          boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
          animation: "fadeIn 0.18s ease-out",
        }}
      >
        {/* Cabecera */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 22,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                background: "rgba(249,115,22,0.15)",
                border: "1px solid rgba(249,115,22,0.25)",
                borderRadius: 9,
                padding: "9px 11px",
                color: "#f97316",
              }}
            >
              <i
                className="fa-solid fa-file-pdf"
                style={{ fontSize: "1.15rem" }}
              />
            </div>
            <div>
              <h3
                style={{
                  margin: 0,
                  color: "var(--text-primary)",
                  fontSize: "1.02rem",
                  fontWeight: 700,
                }}
              >
                {t("recibos.estadoCuenta")}
              </h3>
              <p
                style={{
                  margin: "2px 0 0",
                  color: "var(--text-muted)",
                  fontSize: "0.78rem",
                }}
              >
                {t("recibos.estadoCuentaDesc")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-muted)",
              cursor: "pointer",
              fontSize: "1.15rem",
              lineHeight: 1,
              padding: "2px 4px",
              marginTop: 2,
              transition: "color 0.15s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
            onMouseOut={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
          >
            ✕
          </button>
        </div>

        {/* Separador */}
        <div style={{ borderTop: "1px solid var(--border-card)", marginBottom: 20 }} />

        {/* Campos */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Selector de Filial */}
          <div>
            <label style={fieldLabel}>{t("common.filial")}</label>
            <select
              value={filial}
              onChange={(e) => setFilial(e.target.value)}
              style={{ ...inputStyle, cursor: "pointer" }}
            >
              {filiales.length === 0 && (
                <option value="">{t("common.cargandoDatos")}</option>
              )}
              {filiales.map((f) => (
                <option
                  key={f.id}
                  value={String(f.id)}
                >

                  {f.idFilial}
                  {f.propietarios?.length > 0
                    ? ` — ${f.propietarios[0].nombre ?? ""}`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Fechas en grid */}
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={fieldLabel}>{t("common.fechaDesde")}</label>
              <input
                type="text"
                placeholder="DD/MM/YYYY"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e) => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
            <div>
              <label style={fieldLabel}>{t("common.fechaHasta")}</label>
              <input
                type="text"
                placeholder="DD/MM/YYYY"
                value={fechaFinal}
                onChange={(e) => setFechaFinal(e.target.value)}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                onBlur={(e) => (e.target.style.borderColor = "var(--border-card)")}
              />
            </div>
          </div>

          {/* Nota de formato */}
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "0.75rem" }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: 5 }} />
            {t("recibos.formatoFechas")}
          </p>

          {/* Error */}
          {error && (
            <div
              style={{
                background: "rgba(248,113,113,0.1)",
                border: "1px solid rgba(248,113,113,0.3)",
                borderLeft: "3px solid #f87171",
                borderRadius: 6,
                padding: "8px 12px",
                color: "#fca5a5",
                fontSize: "0.82rem",
              }}
            >
              <i
                className="fa-solid fa-triangle-exclamation"
                style={{ marginRight: 5 }}
              />
              {error}
            </div>
          )}
        </div>

        {/* Separador */}
        <div
          style={{ borderTop: "1px solid var(--border-card)", margin: "20px 0 16px" }}
        />

        {/* Acciones */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: "8px 16px",
              background: "transparent",
              border: "1px solid var(--border-card)",
              borderRadius: 7,
              color: "var(--text-muted)",
              fontSize: "0.84rem",
              cursor: "pointer",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = "var(--text-muted)";
              e.currentTarget.style.color = "var(--text-secondary)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = "var(--border-card)";
              e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            {t("common.cancelar")}
          </button>
          <button
            onClick={handleGenerate}
            disabled={loading || filiales.length === 0}
            style={{
              padding: "8px 20px",
              background: loading ? "rgba(249,115,22,0.6)" : "#f97316",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              fontSize: "0.84rem",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: 7,
              transition: "background 0.2s",
            }}
            onMouseOver={(e) => {
              if (!loading) e.currentTarget.style.background = "#ea6b0c";
            }}
            onMouseOut={(e) => {
              if (!loading) e.currentTarget.style.background = "#f97316";
            }}
          >
            {loading ? (
              <>
                <i className="fa-solid fa-circle-notch fa-spin" /> {t("common.cargando")}
              </>
            ) : (
              <>
                <i className="fa-solid fa-download" /> {t("common.descargarPDF")}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Modal Saldos Iniciales ────────────────────────────────────────────────
function SaldoInicialModal({ onClose, onSuccess }) {
  const [tab, setTab]               = useState("importar");
  const [filiales, setFiliales]     = useState([]);
  const [saldos, setSaldos]         = useState([]);
  const [loadingSaldos, setLoadingSaldos] = useState(false);

  /* ── Import tab ─────────────────────────────── */
  const [downloading, setDownloading]   = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  /* ── Gestionar tab ──────────────────────────── */
  const FORM_INIT = {
    filial: "", tipo: "favor", descripcion: "",
    fecha: "", monto: "", moneda: "CRC", tipo_cambio: "1",
  };
  const [showForm, setShowForm]     = useState(false);
  const [editingPk, setEditingPk]   = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [formMsg, setFormMsg]       = useState(null);
  const [form, setForm]             = useState(FORM_INIT);

  /* ── Load filiales once ─────────────────────── */
  useEffect(() => {
    apiFetch("/api/residents/filial/")
      .then((r) => r.json())
      .then((data) => {
        const list = data.results ?? data;
        setFiliales(list);
        setForm((f) => ({ ...f, filial: list[0] ? String(list[0].id) : "" }));
      })
      .catch(() => {});
  }, []);

  /* ── Load saldos on tab ─────────────────────── */
  useEffect(() => {
    if (tab === "gestionar") loadSaldos();
  }, [tab]);

  function loadSaldos() {
    setLoadingSaldos(true);
    apiFetch("/api/finance/saldo-inicial/")
      .then((r) => r.json())
      .then((data) => setSaldos(Array.isArray(data) ? data : []))
      .catch(() => setSaldos([]))
      .finally(() => setLoadingSaldos(false));
  }

  /* ── Download template ──────────────────────── */
  async function downloadPlantilla() {
    setDownloading(true);
    try {
      const res  = await apiFetch("/api/finance/saldo-inicial/plantilla/");
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "plantilla_saldos_iniciales.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
    } finally {
      setDownloading(false);
    }
  }

  /* ── Upload Excel ───────────────────────────── */
  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      // Usar uploadExcel (fetch directo sin Content-Type forzado) para que el
      // browser pueda establecer el boundary de multipart/form-data correctamente.
      const data = await uploadExcel("/api/finance/saldo-inicial/importar/", file);
      setUploadResult(data);
      if ((data.creados ?? 0) > 0) onSuccess?.();
    } catch (_) {
      setUploadResult({ creados: 0, errores: ["Error de conexión"], omitidos: 0 });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  /* ── Save (create / edit) ───────────────────── */
  async function handleSave() {
    if (!form.filial || !form.tipo || !form.descripcion || !form.fecha || !form.monto) {
      setFormMsg({ ok: false, text: "Todos los campos son requeridos" });
      return;
    }
    setSaving(true);
    setFormMsg(null);
    try {
      const url    = editingPk
        ? `/api/finance/saldo-inicial/${editingPk}/?tipo=${form.tipo}`
        : "/api/finance/saldo-inicial/";
      const method = editingPk ? "PATCH" : "POST";
      const res    = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setFormMsg({ ok: false, text: err.error || "Error al guardar" });
        return;
      }
      setFormMsg({ ok: true, text: editingPk ? "Actualizado" : "Creado correctamente" });
      setShowForm(false);
      setEditingPk(null);
      setForm({ ...FORM_INIT, filial: filiales[0] ? String(filiales[0].id) : "" });
      loadSaldos();
      onSuccess?.();
      setTimeout(() => setFormMsg(null), 3500);
    } catch (_) {
      setFormMsg({ ok: false, text: "Error de conexión" });
    } finally {
      setSaving(false);
    }
  }

  /* ── Delete ─────────────────────────────────── */
  async function handleDelete() {
    if (!deletingItem) return;
    try {
      await apiFetch(
        `/api/finance/saldo-inicial/${deletingItem.pk}/?tipo=${deletingItem.tipo}`,
        { method: "DELETE" }
      );
      setDeletingItem(null);
      loadSaldos();
      onSuccess?.();
    } catch (_) {}
  }

  function startEdit(item) {
    setEditingPk(item.pk);
    setForm({
      filial:       String(item.filial_id),
      tipo:         item.tipo,
      descripcion:  item.descripcion || "",
      fecha:        item.fecha || "",
      monto:        String(item.monto),
      moneda:       item.moneda || "CRC",
      tipo_cambio:  String(item.tipo_cambio ?? 1),
    });
    setShowForm(true);
    setFormMsg(null);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingPk(null);
    setForm({ ...FORM_INIT, filial: filiales[0] ? String(filiales[0].id) : "" });
    setFormMsg(null);
  }

  /* ── Common styles ──────────────────────────── */
  const inputSt = {
    width: "100%", padding: "7px 10px",
    background: "var(--bg-input)", border: "1px solid var(--border-card)",
    borderRadius: 6, color: "var(--text-primary)", fontSize: "0.84rem",
    colorScheme: "light dark", outline: "none",
  };
  const labelSt = {
    display: "block", color: "var(--text-muted)",
    fontSize: "0.71rem", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4,
  };

  /* ── Render ─────────────────────────────────── */
  return (
    <>
      {/* Overlay */}
      <div
        onClick={deletingItem ? undefined : onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.65)",
          zIndex: 1050,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Delete confirmation */}
      {deletingItem && (
        <div style={{
          position: "fixed", top: "50%", left: "50%",
          transform: "translate(-50%,-50%)",
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 12, padding: "24px 28px", zIndex: 1200,
          width: "min(400px,92vw)", boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
        }}>
          <p style={{ color: "var(--text-primary)", fontWeight: 600, marginBottom: 8 }}>
            ¿Eliminar saldo inicial?
          </p>
          <p style={{ color: "var(--text-muted)", fontSize: "0.84rem", marginBottom: 20 }}>
            <strong>{deletingItem.filial}</strong> — {deletingItem.descripcion} ({deletingItem.tipo === "favor" ? "A Favor" : "Pendiente"})
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => setDeletingItem(null)} style={{
              padding: "7px 16px", background: "transparent",
              border: "1px solid var(--border-card)", borderRadius: 6,
              color: "var(--text-muted)", cursor: "pointer",
            }}>Cancelar</button>
            <button onClick={handleDelete} style={{
              padding: "7px 16px", background: "#ef4444", color: "#fff",
              border: "none", borderRadius: 6, fontWeight: 600, cursor: "pointer",
            }}>Eliminar</button>
          </div>
        </div>
      )}

      {/* Main modal */}
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 14, width: "min(760px,95vw)",
        maxHeight: "88vh", overflow: "hidden",
        display: "flex", flexDirection: "column",
        zIndex: 1100, boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
        animation: "fadeIn 0.18s ease-out",
      }}>

        {/* ── Header ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "18px 24px 14px", borderBottom: "1px solid var(--border-card)",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)",
              borderRadius: 9, padding: "9px 11px", color: "#a78bfa",
            }}>
              <i className="fa-solid fa-scale-balanced" style={{ fontSize: "1.1rem" }} />
            </div>
            <div>
              <h3 style={{ margin: 0, color: "var(--text-primary)", fontSize: "1rem", fontWeight: 700 }}>
                Saldos Iniciales
              </h3>
              <p style={{ margin: "2px 0 0", color: "var(--text-muted)", fontSize: "0.77rem" }}>
                Importar o gestionar balances históricos de filiales
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", color: "var(--text-muted)",
            cursor: "pointer", fontSize: "1.1rem", lineHeight: 1, padding: "2px 5px",
          }}>✕</button>
        </div>

        {/* ── Tabs ── */}
        <div style={{
          display: "flex", gap: 0, borderBottom: "1px solid var(--border-card)",
          flexShrink: 0, padding: "0 24px",
        }}>
          {[
            { id: "importar",  icon: "fa-file-arrow-up", label: "Importar Excel" },
            { id: "gestionar", icon: "fa-list-check",    label: "Gestionar"      },
          ].map(({ id, icon, label }) => (
            <button
              key={id}
              onClick={() => { setTab(id); setFormMsg(null); }}
              style={{
                background: "none", border: "none",
                borderBottom: tab === id ? "2px solid #a78bfa" : "2px solid transparent",
                color: tab === id ? "#a78bfa" : "var(--text-muted)",
                fontWeight: tab === id ? 700 : 400,
                fontSize: "0.84rem", padding: "10px 16px",
                cursor: "pointer", marginBottom: -1,
                transition: "color 0.15s",
              }}
            >
              <i className={`fa-solid ${icon}`} style={{ marginRight: 7 }} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Body ── */}
        <div style={{ overflowY: "auto", flex: 1, padding: "20px 24px" }}>

          {/* ═══════════ TAB: IMPORTAR ═══════════ */}
          {tab === "importar" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Info */}
              <div style={{
                background: "rgba(139,92,246,0.07)", border: "1px solid rgba(139,92,246,0.2)",
                borderRadius: 8, padding: "12px 16px", fontSize: "0.83rem",
                color: "var(--text-secondary)", lineHeight: 1.55,
              }}>
                <i className="fa-solid fa-circle-info" style={{ color: "#a78bfa", marginRight: 8 }} />
                Descargá la plantilla, completá los saldos de cada filial y subí el archivo.
                Los tipos <strong>"A Favor"</strong> crean un crédito (saldo negativo en CxC)
                y los <strong>"Pendiente"</strong> crean una factura de saldo inicial.
              </div>

              {/* Paso 1 — Descargar */}
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: "0.78rem", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                  Paso 1 — Descargar plantilla
                </p>
                <button
                  onClick={downloadPlantilla}
                  disabled={downloading}
                  style={{
                    padding: "9px 20px", background: "rgba(139,92,246,0.15)",
                    border: "1px solid rgba(139,92,246,0.4)", color: "#a78bfa",
                    borderRadius: 7, fontWeight: 600, fontSize: "0.84rem",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                  }}
                >
                  {downloading
                    ? <><i className="fa-solid fa-circle-notch fa-spin" /> Descargando…</>
                    : <><i className="fa-solid fa-download" /> Descargar Plantilla (.xlsx)</>}
                </button>
              </div>

              {/* Paso 2 — Subir */}
              <div>
                <p style={{ color: "var(--text-muted)", fontSize: "0.78rem", fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                  Paso 2 — Subir plantilla completada
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  id="si-file-input"
                />
                <label
                  htmlFor="si-file-input"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8,
                    padding: "9px 20px",
                    background: uploading ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.15)",
                    border: "1px solid rgba(139,92,246,0.4)", color: "#a78bfa",
                    borderRadius: 7, fontWeight: 600, fontSize: "0.84rem",
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                >
                  {uploading
                    ? <><i className="fa-solid fa-circle-notch fa-spin" /> Importando…</>
                    : <><i className="fa-solid fa-file-arrow-up" /> Seleccionar archivo Excel</>}
                </label>
              </div>

              {/* Resultado */}
              {uploadResult && (
                <div style={{
                  background: uploadResult.creados > 0
                    ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)",
                  border: `1px solid ${uploadResult.creados > 0
                    ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
                  borderLeft: `4px solid ${uploadResult.creados > 0 ? "#4ade80" : "#f87171"}`,
                  borderRadius: 8, padding: "14px 16px",
                }}>
                  <p style={{
                    fontWeight: 700, fontSize: "0.88rem", margin: "0 0 6px",
                    color: uploadResult.creados > 0 ? "#86efac" : "#fca5a5",
                  }}>
                    {uploadResult.creados > 0
                      ? `✓ ${uploadResult.creados} saldo(s) importado(s) correctamente`
                      : "No se importó ningún saldo"}
                    {uploadResult.omitidos > 0 && ` · ${uploadResult.omitidos} fila(s) vacías omitidas`}
                  </p>
                  {uploadResult.errores?.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 18, color: "#fca5a5", fontSize: "0.8rem" }}>
                      {uploadResult.errores.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ═══════════ TAB: GESTIONAR ═══════════ */}
          {tab === "gestionar" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Toast */}
              {formMsg && (
                <div style={{
                  padding: "9px 14px", borderRadius: 7, fontSize: "0.84rem", fontWeight: 600,
                  background: formMsg.ok ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
                  border: `1px solid ${formMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
                  color: formMsg.ok ? "#4ade80" : "#f87171",
                }}>
                  <i className={`fa-solid ${formMsg.ok ? "fa-circle-check" : "fa-circle-exclamation"}`}
                    style={{ marginRight: 7 }} />
                  {formMsg.text}
                </div>
              )}

              {/* ── Formulario crear/editar ── */}
              {showForm ? (
                <div style={{
                  background: "var(--hover-bg)", border: "1px solid var(--border-card)",
                  borderRadius: 10, padding: "16px 18px",
                }}>
                  <p style={{ color: "#a78bfa", fontWeight: 700, fontSize: "0.88rem", marginBottom: 14 }}>
                    <i className={`fa-solid ${editingPk ? "fa-pen" : "fa-plus"}`} style={{ marginRight: 7 }} />
                    {editingPk ? "Editar saldo inicial" : "Nuevo saldo inicial"}
                  </p>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {/* Filial */}
                    <div>
                      <label style={labelSt}>Filial</label>
                      <select
                        value={form.filial}
                        onChange={(e) => setForm((f) => ({ ...f, filial: e.target.value }))}
                        disabled={!!editingPk}
                        style={{ ...inputSt, cursor: editingPk ? "not-allowed" : "pointer" }}
                      >
                        {filiales.map((fil) => (
                          <option key={fil.id} value={String(fil.id)}>
                            {fil.idFilial}
                            {fil.propietarios?.length > 0 ? ` — ${fil.propietarios[0].nombre ?? ""}` : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Tipo */}
                    <div>
                      <label style={labelSt}>Tipo</label>
                      <select
                        value={form.tipo}
                        onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}
                        style={{ ...inputSt, cursor: "pointer" }}
                      >
                        <option value="favor">A Favor (crédito)</option>
                        <option value="pendiente">Pendiente (débito)</option>
                      </select>
                    </div>
                    {/* Fecha */}
                    <div>
                      <label style={labelSt}>Fecha (DD/MM/AAAA)</label>
                      <input
                        type="text"
                        placeholder="31/01/2026"
                        value={form.fecha}
                        onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))}
                        style={inputSt}
                      />
                    </div>
                    {/* Monto */}
                    <div>
                      <label style={labelSt}>Monto</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={form.monto}
                        onChange={(e) => setForm((f) => ({ ...f, monto: e.target.value }))}
                        style={inputSt}
                      />
                    </div>
                    {/* Descripción (full width) */}
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={labelSt}>Descripción</label>
                      <input
                        type="text"
                        placeholder="Saldo inicial al 31/01/2026"
                        value={form.descripcion}
                        onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
                        style={inputSt}
                      />
                    </div>
                    {/* Moneda + TC */}
                    <div>
                      <label style={labelSt}>Moneda</label>
                      <select
                        value={form.moneda}
                        onChange={(e) => setForm((f) => ({ ...f, moneda: e.target.value }))}
                        style={{ ...inputSt, cursor: "pointer" }}
                      >
                        <option value="CRC">CRC — Colones</option>
                        <option value="USD">USD — Dólares</option>
                      </select>
                    </div>
                    {form.moneda === "USD" && (
                      <div>
                        <label style={labelSt}>Tipo de Cambio</label>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          placeholder="510.00"
                          value={form.tipo_cambio}
                          onChange={(e) => setForm((f) => ({ ...f, tipo_cambio: e.target.value }))}
                          style={inputSt}
                        />
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 14 }}>
                    <button onClick={cancelForm} style={{
                      padding: "7px 16px", background: "transparent",
                      border: "1px solid var(--border-card)", borderRadius: 6,
                      color: "var(--text-muted)", cursor: "pointer", fontSize: "0.84rem",
                    }}>Cancelar</button>
                    <button onClick={handleSave} disabled={saving} style={{
                      padding: "7px 18px", background: saving ? "rgba(139,92,246,0.5)" : "#7c3aed",
                      border: "none", borderRadius: 6, color: "#fff",
                      fontWeight: 600, fontSize: "0.84rem",
                      cursor: saving ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: 7,
                    }}>
                      {saving
                        ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
                        : <><i className="fa-solid fa-floppy-disk" /> {editingPk ? "Guardar cambios" : "Crear"}</>}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setShowForm(true); setEditingPk(null); setFormMsg(null);
                    setForm({ ...FORM_INIT, filial: filiales[0] ? String(filiales[0].id) : "" }); }}
                  style={{
                    alignSelf: "flex-start", padding: "8px 16px",
                    background: "rgba(139,92,246,0.15)",
                    border: "1px solid rgba(139,92,246,0.35)", color: "#a78bfa",
                    borderRadius: 7, fontWeight: 600, fontSize: "0.84rem", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: 7,
                  }}
                >
                  <i className="fa-solid fa-plus" /> Agregar saldo inicial
                </button>
              )}

              {/* ── Tabla de saldos ── */}
              {loadingSaldos ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "var(--text-muted)" }}>
                  <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 8 }} />
                  Cargando…
                </div>
              ) : saldos.length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "30px 0",
                  color: "var(--text-muted)", fontSize: "0.85rem",
                }}>
                  <i className="fa-solid fa-inbox" style={{ fontSize: "1.8rem", marginBottom: 8, display: "block" }} />
                  No hay saldos iniciales registrados
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.83rem" }}>
                    <thead>
                      <tr style={{ background: "var(--hover-bg)" }}>
                        {["Filial", "Tipo", "Fecha", "Descripción", "Monto", ""].map((h) => (
                          <th key={h} style={{
                            padding: "9px 12px", textAlign: "left",
                            color: "var(--text-muted)", fontWeight: 700,
                            fontSize: "0.75rem", textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {saldos.map((s, i) => (
                        <tr key={`${s.tipo}-${s.pk}`} style={{
                          borderBottom: "1px solid var(--border-card)",
                          background: i % 2 === 0 ? "transparent" : "var(--hover-bg)",
                        }}>
                          <td style={{ padding: "9px 12px", fontWeight: 700, color: "var(--text-primary)", whiteSpace: "nowrap" }}>
                            {s.filial}
                          </td>
                          <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                            <span style={{
                              padding: "2px 9px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700,
                              background: s.tipo === "favor"
                                ? "rgba(74,222,128,0.12)" : "rgba(251,146,60,0.12)",
                              color: s.tipo === "favor" ? "#4ade80" : "#fb923c",
                              border: `1px solid ${s.tipo === "favor"
                                ? "rgba(74,222,128,0.3)" : "rgba(251,146,60,0.3)"}`,
                            }}>
                              {s.tipo === "favor" ? "A Favor" : "Pendiente"}
                            </span>
                          </td>
                          <td style={{ padding: "9px 12px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                            {s.fecha}
                          </td>
                          <td style={{
                            padding: "9px 12px", color: "var(--text-secondary)",
                            maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {s.descripcion || <span style={{ color: "var(--text-muted)" }}>—</span>}
                          </td>
                          <td style={{
                            padding: "9px 12px", fontFamily: "monospace", fontWeight: 600,
                            color: s.tipo === "favor" ? "#4ade80" : "#fb923c",
                            whiteSpace: "nowrap",
                          }}>
                            {(s.moneda || "CRC") === "USD"
                              ? `$ ${Number(s.monto).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
                              : `₡ ${Number(s.monto).toLocaleString("es-ES", { minimumFractionDigits: 2 })}`}
                          </td>
                          <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button
                                onClick={() => startEdit(s)}
                                title="Editar"
                                style={{
                                  background: "transparent",
                                  border: "1px solid var(--border-card)",
                                  borderRadius: 5, padding: "4px 9px",
                                  color: "var(--text-muted)", cursor: "pointer",
                                  fontSize: "0.8rem",
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.color = "#a78bfa"; e.currentTarget.style.borderColor = "#a78bfa"; }}
                                onMouseOut={(e)  => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border-card)"; }}
                              >
                                <i className="fa-solid fa-pen-to-square" />
                              </button>
                              <button
                                onClick={() => setDeletingItem(s)}
                                title="Eliminar"
                                style={{
                                  background: "transparent",
                                  border: "1px solid var(--border-card)",
                                  borderRadius: 5, padding: "4px 9px",
                                  color: "var(--text-muted)", cursor: "pointer",
                                  fontSize: "0.8rem",
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.borderColor = "#f87171"; }}
                                onMouseOut={(e)  => { e.currentTarget.style.color = "var(--text-muted)"; e.currentTarget.style.borderColor = "var(--border-card)"; }}
                              >
                                <i className="fa-solid fa-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Banner resultado de importación ──────────────────────────────────────
function ImportResultBanner({ result, pending, onDismiss, onNavigate }) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        width: "100%",
        borderRadius: "8px",
        marginBottom: "14px",
        background: result.ok
          ? "rgba(74,222,128,0.07)"
          : "rgba(248,113,113,0.07)",
        border: `1px solid ${result.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
        borderLeft: `4px solid ${result.ok ? "#4ade80" : "#f87171"}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
          borderBottom:
            pending.length > 0 || result.errores?.length > 0
              ? "1px solid var(--border-card)"
              : "none",
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: "0.88rem",
            color: result.ok ? "#bbf7d0" : "#fecaca",
          }}
        >
          {result.msg}
        </span>
        <button
          onClick={onDismiss}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-muted)",
            cursor: "pointer",
            fontSize: "1rem",
            lineHeight: 1,
            padding: "0 4px",
          }}
        >
          ✕
        </button>
      </div>

      {result.errores?.length > 0 && (
        <div
          style={{
            padding: "10px 16px",
            borderBottom:
              pending.length > 0 ? "1px solid rgba(55,65,81,0.4)" : "none",
          }}
        >
          <details>
            <summary
              style={{
                cursor: "pointer",
                color: "#fb923c",
                fontWeight: 600,
                fontSize: "0.82rem",
              }}
            >
              {result.errores.length} {t("recibos.filasConErrores")}
            </summary>
            <ul
              style={{
                marginTop: 8,
                paddingLeft: 18,
                color: "#fca5a5",
                fontSize: "0.8rem",
              }}
            >
              {result.errores.map((e, i) => (
                <li key={i}>
                  <strong>{t("common.filas")} {e.fila}:</strong> {e.errores?.join(", ") ?? e}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <div
            style={{
              padding: "10px 16px 6px",
              color: "#fdba74",
              fontWeight: 600,
              fontSize: "0.82rem",
            }}
          >
            <i
              className="fa-solid fa-triangle-exclamation"
              style={{ marginRight: 6 }}
            />
            {pending.length} {t("recibos.importadosSinFactura")}
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.84rem",
            }}
          >
            <thead>
              <tr
                style={{ background: "var(--hover-bg)", color: "var(--text-secondary)" }}
              >
                {[
                  t("recibos.numRecibo"),
                  t("common.filial"),
                  t("common.monto"),
                  t("common.fecha"),
                  "Documento/Ref.",
                  t("common.acciones"),
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "8px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      fontSize: "0.78rem",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pending.map((p, idx) => (
                <tr
                  key={p.id}
                  style={{
                    borderBottom: "1px solid var(--border-card)",
                    background:
                      idx % 2 === 0 ? "transparent" : "var(--hover-bg)",
                  }}
                >
                  <td
                    style={{
                      padding: "9px 16px",
                      color: "var(--text-primary)",
                      fontWeight: 600,
                    }}
                  >
                    {p.numero_recibo}
                  </td>
                  <td style={{ padding: "9px 16px", color: "var(--text-secondary)" }}>
                    {p.filial_label}
                  </td>
                  <td>
                    <div style={{ padding:"9px 16px", textAlign:"right",
                      fontFamily:"monospace", fontWeight:600, color:"#9ca3af" }}>
                      {(p.moneda_pago || 'CRC').toUpperCase() === 'USD'
                        ? `$${Number(p.monto).toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}`
                        : `₡${Number(p.monto).toLocaleString("es-ES", { minimumFractionDigits:2, maximumFractionDigits:2 })}`}
                    </div>
                  </td>
                  <td style={{ padding: "9px 16px", color: "var(--text-secondary)" }}>
                    {p.fecha_pago}
                  </td>
                  <td style={{ padding: "9px 16px", color: "var(--text-secondary)",
                    maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.documento || <span style={{color:"var(--text-muted)"}}>—</span>}
                  </td>
                  <td style={{ padding: "9px 16px" }}>
                    <button
                      onClick={() => onNavigate(p.id)}
                      style={{
                        padding: "4px 14px",
                        background: "#f97316",
                        color: "#fff",
                        border: "none",
                        borderRadius: 5,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        transition: "background 0.2s",
                        whiteSpace: "nowrap",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.background = "#c2570c")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.background = "#f97316")
                      }
                    >
                      {t("recibos.aplicar")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              padding: "6px 16px",
              color: "var(--text-muted)",
              fontSize: "0.76rem",
              textAlign: "right",
            }}
          >
            {t("recibos.tambiénAparecen")}
          </div>
        </div>
      )}
    </div>
  );
}
